import type React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface PrivateRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
  requiredPermissions?: string[];
  requireAll?: boolean;
}

/**
 * PrivateRoute component - Wrapper for protected routes
 * Redirects to login if not authenticated
 * Optionally checks for required permissions
 */
export const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  requiredPermission,
  requiredPermissions,
  requireAll = false,
}) => {
  const { isAuthenticated, loading, hasPermission, hasAnyPermission, hasAllPermissions } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check single permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div className="access-denied-container">
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
        <p>
          Required permission: <strong>{requiredPermission}</strong>
        </p>
      </div>
    );
  }

  // Check multiple permissions
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasAccess = requireAll ? hasAllPermissions(requiredPermissions) : hasAnyPermission(requiredPermissions);

    if (!hasAccess) {
      return (
        <div className="access-denied-container">
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
          <p>
            Required permissions: <strong>{requiredPermissions.join(', ')}</strong>
          </p>
          <p>
            Mode: <strong>{requireAll ? 'All required' : 'Any required'}</strong>
          </p>
        </div>
      );
    }
  }

  // User is authenticated and has required permissions
  return children;
};
